import { type FC, useMemo, useRef } from "react";
import { Field, Form } from "react-final-form";
import Grid from "@mui/material/Grid";
import {
  Button,
  Card,
  CardContent,
  FormControl,
  FormGroup,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { DateTimePicker, Switch } from "@/shared/components/Form";
import { ShareUrl } from "@/shared/components/ShareUrl";
import { useIsMobile } from "@/shared/hooks";
import {
  PreviewImageField,
  TagsByTypeField,
  EmbedCode,
  WidgetField,
} from "@/partner/features/videos";
import { ProductsField } from "@/partner/features/products";
import { useCurrentPlatformQuery } from "@/partner/entities/platforms";
import type { Stream, StreamContent } from "@/partner/entities/streams";
import { useUpdateStreamMutation } from "@/partner/entities/streams";
import { StreamStatusType } from "@/types/graphql/partner.generated";
import type { StreamFormValues } from "../model";
import { StreamDialogHeader } from "./StreamDialogHeader";
import { StreamPlayerPreview } from "./StreamPlayerPreview";
import { StreamEventCancelMenu } from "./StreamEventCancelMenu";

const FORM_ID = "stream-edit-form";
// Совпадает с SPACING/FORM_SPACING референсного VideoForm.tsx — та же
// компоновка формы (хедер + двухколоночный Grid), просто с полями стрима.
const SPACING = 1;
const FORM_SPACING = 2;
const AGE_RESTRICTIONS = [6, 12, 16, 18];

interface StreamEditTabProps {
  content: StreamContent;
  stream: Stream;
  onClose: () => void;
}

export const StreamEditTab: FC<StreamEditTabProps> = ({
  content,
  stream,
  onClose,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  // Совпадает с референсным useIsMobile("sm") — переворот колонок на
  // мобильном происходит на брейкпоинте 600px, а не на дефолтных 768px хука.
  const isMobile = useIsMobile("(max-width: 600px)");
  // Референсный DialogHeaderButton.js: адаптивный размер submit-кнопок
  // хедера на брейкпоинте md (900px) — наш useIsMobile по умолчанию режет
  // на 768px, поэтому берём брейкпоинт темы напрямую.
  const isCompactHeader = useMediaQuery(theme.breakpoints.down("md"));
  const { data: platformData } = useCurrentPlatformQuery();
  const isStreamChatEnabled = Boolean(
    platformData?.currentPlatform?.isStreamChatEnabled
  );
  const isDraft = !content.publishAt;
  // true = «Опубликовать» (publishAt=now при пустом), false = «Черновик»/
  // «Скрыть» (publishAt=null) — выставляется onClick submit-кнопки, как в
  // VideoForm.publishIntentRef.
  const publishIntentRef = useRef(!isDraft);

  const [updateStream, { loading }] = useUpdateStreamMutation({
    onCompleted: () => {
      // UpdateStreamMutation возвращает только { id } — без refetchQueries
      // изменённые поля content (publishAt и т.д.) не попадут в кэш, а диалог
      // остаётся открытым (см. EditVideoDialog.useUpdateContentMutation).
      enqueueSnackbar(t("streams.form.savedSnackbar"), { variant: "success" });
    },
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    refetchQueries: ["Stream"],
  });

  // Мемоизация как в референсном EditVideoDialog (initialValues от data) —
  // активная contentStreamStatusChanged-подписка обновляет content/stream в
  // кэше Apollo, из-за чего без useMemo tagIds/productIds получали бы новые
  // ссылки массивов на каждый рендер и провоцировали лишнюю реинициализацию
  // формы.
  const initialValues: StreamFormValues = useMemo(
    () => ({
      title: content.title,
      description: content.description ?? null,
      startAt: stream.startAt ?? null,
      publishAt: content.publishAt ?? null,
      accessStartAt: content.accessStartAt ?? null,
      previewId: content.preview?.id ?? null,
      verticalPreviewId: content.verticalPreview?.id ?? null,
      tagIds: (content.tags ?? []).map((tag) => tag.id),
      productIds: content.productIds ?? [],
      widgetId: content.widget?.id ?? null,
      ageRestrictions: content.ageRestrictions ?? 0,
      hideInGeneralLists: content.hideInGeneralLists,
      onlyUsers: content.onlyUsers,
      enableRecord: stream.enableRecord,
      autoPublishRecord: stream.autoPublishRecord,
      isChatEnabled: stream.isChatEnabled,
      dvrEnabled: stream.dvrEnabled,
    }),
    [content, stream]
  );

  const handleFormSubmit = async (values: StreamFormValues) => {
    const publish = publishIntentRef.current;
    await updateStream({
      variables: {
        contentId: content.id,
        input: {
          ...values,
          publishAt: publish
            ? values.publishAt || new Date().toISOString()
            : null,
        },
      },
    });
  };

  return (
    <Form<StreamFormValues>
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      // Живой статус стрима (contentStreamStatusChanged) пересчитывает
      // content/stream из кэша Apollo — не должно затирать уже введённые
      // пользователем несохранённые правки формы (см. EditVideoDialog).
      keepDirtyOnReinitialize
      subscription={{ values: true }}
      render={({ handleSubmit, values }) => (
        <form id={FORM_ID} onSubmit={handleSubmit}>
          <StreamDialogHeader
            title={t("streams.form.dialogTitle")}
            onClose={onClose}
          >
            <Stack direction="row" spacing={1}>
              <Tooltip
                title={
                  isDraft
                    ? t("streams.form.publishTooltip")
                    : t("streams.form.saveChangesTooltip")
                }
              >
                <span>
                  <Button
                    variant="contained"
                    type="submit"
                    form={FORM_ID}
                    disabled={loading}
                    size={isCompactHeader ? "small" : "medium"}
                    disableElevation
                    sx={{ fontSize: isCompactHeader ? "0.7rem" : undefined }}
                    onClick={() => {
                      publishIntentRef.current = true;
                    }}
                  >
                    {isDraft
                      ? t("common.actions.publish")
                      : t("common.actions.save")}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  isDraft
                    ? t("streams.form.saveDraftTooltip")
                    : t("streams.form.hideTooltip")
                }
              >
                <span>
                  <Button
                    variant="contained"
                    type="submit"
                    form={FORM_ID}
                    disabled={loading}
                    size={isCompactHeader ? "small" : "medium"}
                    disableElevation
                    sx={{ fontSize: isCompactHeader ? "0.7rem" : undefined }}
                    onClick={() => {
                      publishIntentRef.current = false;
                    }}
                  >
                    {isDraft
                      ? t("streams.form.saveDraftButton")
                      : t("common.actions.hide")}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
            {stream.status !== StreamStatusType.CancelledType && (
              <StreamEventCancelMenu productIds={content.productIds ?? []} />
            )}
          </StreamDialogHeader>

          <Grid
            container
            spacing={SPACING}
            flexDirection={isMobile ? "column-reverse" : "row"}
          >
            <Grid size={{ xs: 12, sm: 6, md: 7 }}>
              <Grid container spacing={SPACING}>
                <Grid size={12}>
                  <Card elevation={0}>
                    <CardContent sx={{ pt: 3 }}>
                      <Grid container spacing={FORM_SPACING}>
                        <Grid size={12}>
                          <Field name="title">
                            {({ input, meta }) => (
                              <TextField
                                {...input}
                                autoFocus
                                required
                                label={t("streams.form.titleField")}
                                fullWidth
                                variant="outlined"
                                error={meta.touched && Boolean(meta.error)}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid size={12}>
                          <Field name="description">
                            {({ input }) => (
                              <TextField
                                {...input}
                                label={t("streams.form.descriptionField")}
                                multiline
                                minRows={2}
                                maxRows={10}
                                fullWidth
                                variant="outlined"
                              />
                            )}
                          </Field>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={12}>
                  <Card elevation={0}>
                    <CardContent sx={{ pt: 3 }}>
                      <Grid container spacing={FORM_SPACING}>
                        <Grid size={{ xs: 12, lg: 6 }}>
                          <Grid container spacing={FORM_SPACING}>
                            <Grid size={12}>
                              <DateTimePicker
                                name="startAt"
                                label={t("streams.form.startAtField")}
                                outputFormat="iso"
                              />
                            </Grid>
                            <Grid size={12}>
                              <DateTimePicker
                                name="publishAt"
                                label={t("streams.form.publishAtLabel")}
                                outputFormat="iso"
                              />
                            </Grid>
                            <Grid size={12}>
                              <DateTimePicker
                                name="accessStartAt"
                                label={t("streams.form.accessStartAtField")}
                                outputFormat="iso"
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid size={{ xs: 12, lg: 6 }}>
                          <PreviewImageField />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={12}>
                  <Card elevation={0}>
                    <CardContent sx={{ pt: 3 }}>
                      <Grid container spacing={FORM_SPACING}>
                        <Grid size={12}>
                          <TagsByTypeField />
                        </Grid>
                        <Grid size={{ xs: 12, lg: 6 }}>
                          <Field name="ageRestrictions">
                            {({ input }) => (
                              <TextField
                                select
                                fullWidth
                                variant="outlined"
                                label={t("streams.form.ageRestrictionsField")}
                                value={input.value ?? 0}
                                onChange={(event) =>
                                  input.onChange(Number(event.target.value))
                                }
                              >
                                <MenuItem value={0}>
                                  {t("streams.form.ageRestrictionsNone")}
                                </MenuItem>
                                {AGE_RESTRICTIONS.map((age) => (
                                  <MenuItem key={age} value={age}>
                                    {`${age}+`}
                                  </MenuItem>
                                ))}
                              </TextField>
                            )}
                          </Field>
                        </Grid>
                        <Grid size={12}>
                          <ProductsField />
                        </Grid>
                        <Grid size={12}>
                          <WidgetField />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={12}>
                  <Card elevation={0}>
                    <CardContent sx={{ pt: 2 }}>
                      <FormControl component="fieldset">
                        <FormGroup>
                          <Switch
                            name="hideInGeneralLists"
                            label={t("streams.form.hideInGeneralListsToggle")}
                          />
                          {isStreamChatEnabled && (
                            <Switch
                              name="isChatEnabled"
                              label={t("streams.form.isChatEnabledToggle")}
                            />
                          )}
                          <Switch
                            name="dvrEnabled"
                            label={t("streams.form.dvrEnabledToggle")}
                            helperText={t("streams.form.dvrEnabledHelperText")}
                          />
                          <Switch
                            name="enableRecord"
                            label={t("streams.form.enableRecordToggle")}
                          />
                          {values?.enableRecord && (
                            <Switch
                              name="autoPublishRecord"
                              label={t("streams.form.autoPublishRecordToggle")}
                            />
                          )}
                          <Switch
                            name="onlyUsers"
                            label={t("streams.form.onlyUsersToggle")}
                          />
                        </FormGroup>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 5 }}>
              <Grid container spacing={SPACING}>
                <Grid size={12}>
                  <Card elevation={0}>
                    <StreamPlayerPreview
                      stream={stream}
                      posterUrl={content.preview?.url}
                    />
                  </Card>
                </Grid>
                <Grid size={12}>
                  <Card elevation={0}>
                    <CardContent>
                      <Stack spacing={2}>
                        <ShareUrl
                          url={content.url}
                          label={t("platformCustomCovers.form.link")}
                          copiedMessage={t("products.form.shareUrlCopied")}
                        />
                        <EmbedCode embedUrl={content.embedUrl} />
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      )}
    />
  );
};
